/**
 * RaceIQPanel — concise deterministic insights from the engine (what changed,
 * who is affected, the measured evidence, why it matters, what to watch),
 * plus the three championship views when the snapshot carries them.
 */
import React, { useMemo, useState } from "react";
import { Sparkles, Trophy } from "lucide-react";
import { useRaceSession } from "../RaceSessionProvider";

const KIND_CLASS = { observed: "border-white/10 bg-white/[0.04] text-zinc-200", estimate: "border-amber-400/25 bg-amber-400/10 text-amber-200", scenario: "border-sky-300/25 bg-sky-300/10 text-sky-200" };

function InsightCard({ insight, onJump }) {
    return (
        <button type="button" onClick={() => onJump?.(insight)} className="w-full rounded-2xl border border-white/5 bg-white/[0.02] px-3 py-3 text-left text-sm leading-relaxed text-zinc-300 transition-colors hover:bg-white/[0.035]" data-testid="insight-card">
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">{insight.title}</span>
                <span className={`rounded-full border px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.16em] ${KIND_CLASS[insight.kind] || KIND_CLASS.observed}`}>{insight.kind}</span>
            </div>
            <div>{insight.what_changed}</div>
            {insight.evidence?.length ? <div className="mt-1.5 font-mono text-[10px] text-zinc-500">{insight.evidence.join(" · ")}</div> : null}
            {insight.why_it_matters ? <div className="mt-1.5 text-xs text-zinc-400">{insight.why_it_matters}</div> : null}
            {insight.watch_next ? <div className="mt-1 text-xs text-zinc-500">Watch: {insight.watch_next}</div> : null}
        </button>
    );
}

function Standings({ view }) {
    if (!view?.available) return <div className="text-xs text-zinc-500">{view?.basis || "Unavailable."}</div>;
    return (
        <div className="space-y-1">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-600">{view.basis}</div>
            {view.drivers.slice(0, 8).map((row) => (
                <div key={row.key} className={`flex items-center justify-between font-mono text-[11px] ${row.non_entrant ? "text-zinc-500" : "text-zinc-300"}`} title={row.non_entrant ? "Not entered in this session" : undefined}>
                    <span>
                        <span className="text-zinc-600">{row.position}</span> {row.label}
                        {row.position_change ? <span className={row.position_change > 0 ? "text-emerald-400" : "text-f1-red"}> {row.position_change > 0 ? "▲" : "▼"}{Math.abs(row.position_change)}</span> : null}
                    </span>
                    <span>
                        {row.points}
                        {row.delta_points ? <span className="text-zinc-600"> (+{row.delta_points})</span> : null}
                    </span>
                </div>
            ))}
        </div>
    );
}

export default function RaceIQPanel({ className = "" }) {
    const { snapshot, jumpToEvent, selectedEntryId, cursor } = useRaceSession();
    const [tab, setTab] = useState("insights");
    const insights = useMemo(() => {
        const list = snapshot?.insights || [];
        return list.filter((item) => item.session_seconds == null || item.session_seconds <= cursor).slice(-6).reverse();
    }, [snapshot, cursor]);
    const championship = snapshot?.championship;
    const [view, setView] = useState("projected");
    return (
        <div className={`flex min-h-0 flex-col ${className}`} data-testid="race-iq-panel">
            <div className="mb-3 flex items-center gap-2">
                {["insights", "standings"].map((item) => (
                    <button key={item} type="button" aria-pressed={tab === item} onClick={() => setTab(item)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors ${tab === item ? "bg-white/10 text-white" : "text-zinc-500 hover:text-zinc-200"}`}>
                        {item === "insights" ? <Sparkles className="h-3.5 w-3.5 text-f1-red" /> : <Trophy className="h-3.5 w-3.5 text-zinc-400" />}
                        {item === "insights" ? "Race IQ" : "Championship"}
                    </button>
                ))}
            </div>
            {tab === "insights" ? (
                insights.length ? (
                    <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                        {insights.map((insight) => (
                            <InsightCard key={insight.id} insight={insight} onJump={(item) => jumpToEvent({ session_seconds: item.session_seconds, participants: item.entries })} />
                        ))}
                    </div>
                ) : (
                    <div className="rounded-2xl bg-white/[0.015] px-3 py-4 text-sm leading-relaxed text-zinc-500">{selectedEntryId ? "No insight involves the selected driver yet." : "Insights appear as the engine measures gaps, pit cycles and pace changes."}</div>
                )
            ) : (
                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                    <div className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] p-1 font-mono text-[9px] uppercase tracking-[0.18em]">
                        {["baseline", "projected", "confirmed"].map((item) => (
                            <button key={item} type="button" aria-pressed={view === item} onClick={() => setView(item)} className={`rounded-full px-2.5 py-1 ${view === item ? "bg-white/10 text-white" : "text-zinc-500"}`}>
                                {item}
                            </button>
                        ))}
                    </div>
                    <Standings view={championship?.[view]} />
                    {championship?.reconciliation?.status === "mismatch" ? <div className="text-xs text-f1-red">Scoring reconciliation mismatch: {championship.reconciliation.mismatches.length} rows differ from published points.</div> : null}
                </div>
            )}
        </div>
    );
}
